import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import Icon from './AppIcon';

const NuovaRichiestaAdminModal = ({ isOpen, onClose, idAzienda, adminId, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dipendenti, setDipendenti] = useState([]);
  const [loadingDipendenti, setLoadingDipendenti] = useState(true);

  const [idUtenteSelezionato, setIdUtenteSelezionato] = useState('');
  const [tipo, setTipo] = useState('giorno_singolo');
  const [giornoUnico, setGiornoUnico] = useState('');
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  const [oreDal, setOreDal] = useState('');
  const [oreAl, setOreAl] = useState('');
  const [weekendIncluso, setWeekendIncluso] = useState(false);
  const [turno, setTurno] = useState('Giornaliero');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!isOpen || !idAzienda) return;
    const fetchDipendenti = async () => {
      setLoadingDipendenti(true);
      const { data, error } = await supabase
        .from('profili')
        .select('id, nome, cognome, email, phone')
        .eq('id_azienda', idAzienda)
        .order('nome', { ascending: true });

      if (!error && data) setDipendenti(data);
      setLoadingDipendenti(false);
    };
    fetchDipendenti();
  }, [isOpen, idAzienda]);

  if (!isOpen) return null;

  const resetForm = () => {
    setIdUtenteSelezionato('');
    setTipo('giorno_singolo');
    setGiornoUnico('');
    setDal('');
    setAl('');
    setOreDal('');
    setOreAl('');
    setWeekendIncluso(false);
    setTurno('Giornaliero');
    setNote('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const calcolaGiorni = (dataInizio, dataFine, includiWeekend) => {
    const start = new Date(dataInizio);
    const end = new Date(dataFine);
    let count = 0;
    const current = new Date(start);
    while (current <= end) {
      const day = current.getDay();
      if (includiWeekend || (day !== 0 && day !== 6)) count++;
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!idUtenteSelezionato) {
      return setError(t('ferie.admin.errors.select_employee'));
    }
    if (tipo === 'giorno_singolo' && !giornoUnico) {
      return setError(t('ferie.errors.select_date'));
    }
    if (tipo === 'piu_giorni' && (!dal || !al)) {
      return setError(t('ferie.errors.select_range'));
    }
    if (tipo === 'ore' && (!giornoUnico || !oreDal || !oreAl)) {
      return setError(t('ferie.errors.select_date_time'));
    }

    setLoading(true);
    try {
      const dipendente = dipendenti.find(d => d.id === idUtenteSelezionato);
      if (!dipendente) throw new Error('Dipendente non trovato');

      const { data: { user } } = await supabase.auth.getUser();
      const { data: adminProfilo } = await supabase
        .from('profili')
        .select('nome, cognome')
        .eq('id', user.id)
        .single();
      const adminName = adminProfilo ? `${adminProfilo.nome} ${adminProfilo.cognome}` : 'Admin';

      let payload = {
        id_utente: dipendente.id,
        id_azienda: idAzienda,
        nome: dipendente.nome,
        cognome: dipendente.cognome,
        email: dipendente.email,
        phone: dipendente.phone,
        tipo_richiesta: tipo,
        turno,
        note: note || null,
        weekend_incluso: weekendIncluso,
        accettato: true,
        risposto_da: adminName,
        data_risposta: new Date().toISOString(),
      };

      if (tipo === 'giorno_singolo') {
        payload.dal = giornoUnico;
        payload.al = giornoUnico;
        payload.giorni = 1;
      }

      if (tipo === 'piu_giorni') {
        payload.dal = dal;
        payload.al = al;
        payload.giorni = calcolaGiorni(dal, al, weekendIncluso);
      }

      if (tipo === 'ore') {
        payload.dal = giornoUnico;
        payload.al = giornoUnico;
        payload.ore_dal = oreDal;
        payload.ore_al = oreAl;
        payload.giorni = 0;
      }

      const { error: insertErr } = await supabase.from('ferie').insert([payload]);
      if (insertErr) throw insertErr;

      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      console.error(err);
      setError(t('ferie.errors.submit_error'));
    } finally {
      setLoading(false);
    }
  };

  const TipoBtn = ({ value, label, icon }) => (
    <button
      type="button"
      onClick={() => setTipo(value)}
      className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl font-black text-[11px] uppercase tracking-widest transition-all duration-200 border-2 ${
        tipo === value
          ? 'bg-primary text-white border-primary shadow-lg'
          : 'bg-muted/20 text-muted-foreground border-border/40 hover:border-primary/30'
      }`}
    >
      <Icon name={icon} size={18} />
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-[2px] px-4 animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-lg bg-card rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 flex justify-between items-center border-b border-border">
          <h2 className="font-black uppercase text-sm tracking-widest text-foreground">
            {t('ferie.admin.new_request_title')}
          </h2>
          <button onClick={handleClose} className="p-2 hover:bg-muted rounded-xl transition-colors text-muted-foreground">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl">
              <p className="text-red-500 text-[11px] text-center font-bold uppercase">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">

            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                {t('ferie.admin.select_employee_label')}
              </label>
              {loadingDipendenti ? (
                <div className="flex justify-center py-3">
                  <Icon name="Loader2" size={18} className="animate-spin text-primary/50" />
                </div>
              ) : (
                <select
                  value={idUtenteSelezionato}
                  onChange={(e) => setIdUtenteSelezionato(e.target.value)}
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                  required
                >
                  <option value="">{t('ferie.admin.select_employee_placeholder')}</option>
                  {dipendenti.map(d => (
                    <option key={d.id} value={d.id}>{d.nome} {d.cognome}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="flex gap-2">
              <TipoBtn value="giorno_singolo" label={t('ferie.type_single_day')} icon="Calendar" />
              <TipoBtn value="piu_giorni" label={t('ferie.type_multi_day')} icon="CalendarRange" />
              <TipoBtn value="ore" label={t('ferie.type_hours')} icon="Clock" />
            </div>

            {(tipo === 'giorno_singolo' || tipo === 'ore') && (
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                  {t('ferie.date_label')}
                </label>
                <input
                  type="date"
                  value={giornoUnico}
                  onChange={(e) => setGiornoUnico(e.target.value)}
                  className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                  required
                />
              </div>
            )}

            {tipo === 'piu_giorni' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.from_label')}
                  </label>
                  <input
                    type="date"
                    value={dal}
                    onChange={(e) => setDal(e.target.value)}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.to_label')}
                  </label>
                  <input
                    type="date"
                    value={al}
                    onChange={(e) => setAl(e.target.value)}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    required
                  />
                </div>
              </div>
            )}

            {tipo === 'ore' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.from_time_label')}
                  </label>
                  <input
                    type="time"
                    value={oreDal}
                    onChange={(e) => setOreDal(e.target.value)}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                    {t('ferie.to_time_label')}
                  </label>
                  <input
                    type="time"
                    value={oreAl}
                    onChange={(e) => setOreAl(e.target.value)}
                    className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
                    required
                  />
                </div>
              </div>
            )}

            {tipo === 'piu_giorni' && (
              <label className="flex items-center gap-3 cursor-pointer bg-muted/20 p-4 rounded-xl border border-border/40">
                <input
                  type="checkbox"
                  checked={weekendIncluso}
                  onChange={(e) => setWeekendIncluso(e.target.checked)}
                  className="w-5 h-5 accent-primary"
                />
                <span className="text-[11px] font-black uppercase tracking-widest text-foreground">
                  {t('ferie.weekend_included')}
                </span>
              </label>
            )}

            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                {t('ferie.shift_label')}
              </label>
              <select
                value={turno}
                onChange={(e) => setTurno(e.target.value)}
                className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10"
              >
                <option value="Giornaliero">{t('ferie.shift_daily')}</option>
                <option value="Primo Turno">{t('ferie.shift_first')}</option>
                <option value="Secondo Turno">{t('ferie.shift_second')}</option>
                <option value="Terzo Turno">{t('ferie.shift_third')}</option>
                <option value="Altro">{t('ferie.shift_other')}</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 block">
                {t('ferie.note_label')}
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t('ferie.note_placeholder')}
                className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/10 resize-none"
              />
            </div>

            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
              <Icon name="CheckCircle2" size={18} className="text-emerald-600 shrink-0" />
              <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">
                {t('ferie.admin.auto_accept_notice')}
              </p>
            </div>

            <div className="pt-2 flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-4 rounded-xl font-black uppercase text-[11px] tracking-widest bg-muted text-muted-foreground hover:bg-muted/70 transition-all"
              >
                {t('ferie.cancel_btn')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-4 rounded-xl font-black uppercase text-[11px] tracking-widest bg-primary text-white shadow-lg shadow-primary/20 hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>{t('ferie.submit_btn')}</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NuovaRichiestaAdminModal;